"use client";

import { c as _c } from "react/compiler-runtime";
import InfoIcon from "@/assets/icons/info.svg";
import { ScrollArea, Tooltip } from "@/shared/ui";
import { useMediaQuery } from "@/hooks/use-media-query";
import { breakpointMin } from "@/lib/breakpoints";
import { cn } from "@/lib/utils";
import { useStreamStatsGamesQuery, type StreamStatsGame } from "@/features/stream-stats/api/stream-stats-queries";
import { Skeleton, Stack } from "@mantine/core";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import classes from "./game-stats-card.module.scss";
function GameThumbnailPlaceholder(t0) {
  const $ = _c(2);
  const {
    dimmed: t1
  } = t0;
  const dimmed = t1 === undefined ? false : t1;
  const t2 = dimmed ? classes.emptyThumbImage : classes.thumbPlaceholderImage;
  let t3;
  if ($[0] !== t2) {
    t3 = <div className={classes.thumbPlaceholder}><img src="/images/imagePlaceholder.png" alt="" width={52} height={52} className={t2} /></div>;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  return t3;
}
export type GameStatsSortKey = "-airtime" | "-reach" | "-averageView" | "-peakView";
export type GameStatsRow = {
  id: string;
  title: string;
  thumbnail?: string;
  value: string;
  sharePct: number;
  progressPct: number;
  variant?: "default" | "accent";
};
function formatAirtime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function formatMetric(value: number): string {
  if (value < 1000) return String(Math.round(value));
  const kValue = value / 1000;
  return Number.isInteger(kValue) ? `${kValue}K` : `${kValue.toFixed(1).replace(/\.0$/, "")}K`;
}
function getShareMetric(game: StreamStatsGame, sort: GameStatsSortKey): number {
  switch (sort) {
    case "-airtime":
      return game.shareOfAirtime;
    case "-reach":
      return game.shareOfReach;
    case "-averageView":
      return game.shareOfAverageView;
    case "-peakView":
      return game.shareOfPeakView;
  }
}
function getRelativeMetric(game: StreamStatsGame, sort: GameStatsSortKey): number {
  switch (sort) {
    case "-airtime":
      return game.relativeAirtime;
    case "-reach":
      return game.relativeReach;
    case "-averageView":
      return game.relativeAverageView;
    case "-peakView":
      return game.relativePeakView;
  }
}
function formatSharePct(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.?0+$/, "");
}
function formatGameMetric(game: StreamStatsGame, sort: GameStatsSortKey): string {
  switch (sort) {
    case "-airtime":
      return formatAirtime(game.airtimeMinutes);
    case "-reach":
      return formatMetric(game.reach);
    case "-averageView":
      return formatMetric(game.avgViewers);
    case "-peakView":
      return formatMetric(game.peakViewers);
  }
}
function gamesToRows(games: StreamStatsGame[], sort: GameStatsSortKey): GameStatsRow[] {
  return games.map(g => {
    return {
      id: String(g.gameId),
      title: g.gameName,
      thumbnail: g.displayImage || undefined,
      value: formatGameMetric(g, sort),
      sharePct: getShareMetric(g, sort),
      progressPct: getRelativeMetric(g, sort)
    };
  });
}
function GameStatsCardList(t0) {
  const $ = _c(32);
  const {
    row,
    active,
    onActivate
  } = t0;
  const pct = Math.max(0, Math.min(100, row.progressPct));
  const isAccent = row.variant === "accent";
  const [imgError, setImgError] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${breakpointMin.desktop}px)`);
  let t1;
  if ($[0] !== isDesktop || $[1] !== onActivate) {
    t1 = () => {
      if (isDesktop) {
        return;
      }
      onActivate();
    };
    $[0] = isDesktop;
    $[1] = onActivate;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleClick = t1;
  const t2 = isAccent && classes.rowAccent;
  const t3 = active && classes.rowActive;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = cn(classes.row, t2, t3);
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== pct) {
    t5 = {
      "--progress-pct": pct
    };
    $[6] = pct;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = t5 as CSSProperties;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div aria-hidden={true} className={classes.progressBar} />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== imgError || $[10] !== row.thumbnail) {
    t8 = <div className={classes.thumb}>{row.thumbnail && !imgError ? <img src={row.thumbnail} alt="" width={52} height={52} className={classes.thumbImage} onError={() => setImgError(true)} /> : <GameThumbnailPlaceholder />}</div>;
    $[9] = imgError;
    $[10] = row.thumbnail;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== row.title) {
    t9 = <p className={classes.gameTitle}>{row.title}</p>;
    $[12] = row.title;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== row.value) {
    t10 = <span className={classes.metricValue}>{row.value}</span>;
    $[14] = row.value;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== row.sharePct) {
    t11 = formatSharePct(row.sharePct);
    $[16] = row.sharePct;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11) {
    t12 = <span className={classes.metricPct}>{t11}%</span>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12) {
    t13 = <div className={classes.metric}>{t10}{t12}</div>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t13 || $[24] !== t9) {
    t14 = <div className={classes.rowMeta}>{t9}{t13}</div>;
    $[23] = t13;
    $[24] = t9;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== handleClick || $[27] !== t14 || $[28] !== t4 || $[29] !== t6 || $[30] !== t8) {
    t15 = <div className={t4} style={t6} onClick={handleClick}>{t7}{t8}{t14}</div>;
    $[26] = handleClick;
    $[27] = t14;
    $[28] = t4;
    $[29] = t6;
    $[30] = t8;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
}
function GameStatsScrollList(t0) {
  const $ = _c(20);
  const {
    rows
  } = t0;
  const [activeId, setActiveId] = useState(null);
  const [showScrollFade, setShowScrollFade] = useState(false);
  const viewportRef = useRef(null);
  const isScrollableList = rows.length > 5;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const viewport = viewportRef.current;
      if (!viewport) {
        return;
      }
      setShowScrollFade(viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight > 1);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const syncScrollFade = t1;
  let t2;
  if ($[1] !== isScrollableList) {
    t2 = () => {
      if (!isScrollableList) {
        return;
      }
      const frame = requestAnimationFrame(syncScrollFade);
      return () => cancelAnimationFrame(frame);
    };
    $[1] = isScrollableList;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isScrollableList || $[4] !== rows) {
    t3 = [isScrollableList, rows];
    $[3] = isScrollableList;
    $[4] = rows;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== activeId || $[7] !== rows) {
    let t5;
    if ($[9] !== activeId) {
      t5 = row => <GameStatsCardList key={row.id} row={row} active={activeId === row.id} onActivate={() => setActiveId(id => id === row.id ? null : row.id)} />;
      $[9] = activeId;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = rows.map(t5);
    $[6] = activeId;
    $[7] = rows;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const rowList = t4;
  let t5;
  if ($[11] !== isScrollableList || $[12] !== rowList) {
    t5 = isScrollableList ? <ScrollArea viewportRef={viewportRef} className={classes.listScrollable} scrollHideDelay={0} onScrollPositionChange={syncScrollFade} classNames={{
      scrollbar: classes.listScrollbar
    }}><Stack gap="var(--spacing-md)" className={cn(classes.list, classes.listScrollableContent)}>{rowList}</Stack></ScrollArea> : <Stack gap="var(--spacing-md)" className={classes.list}>{rowList}</Stack>;
    $[11] = isScrollableList;
    $[12] = rowList;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== isScrollableList || $[15] !== showScrollFade) {
    t6 = isScrollableList && showScrollFade ? <div aria-hidden={true} className={classes.listScrollFade} /> : null;
    $[14] = isScrollableList;
    $[15] = showScrollFade;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <div className={classes.listHost}>{t5}{t6}</div>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function GameStatsEmptyRow() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div aria-hidden={true} className={classes.emptyProgress} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={classes.thumb}><GameThumbnailPlaceholder dimmed={true} /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className={classes.row}>{t0}{t1}<div className={classes.rowMeta}><p className={classes.gameTitle}>— —</p><span className={classes.metric}>--</span></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function GameStatsEmptyList() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={classes.listHost}><Stack gap="var(--spacing-md)" className={classes.list}>{Array.from({
          length: 5
        }).map(_temp)}</Stack></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(_, i) {
  return <GameStatsEmptyRow key={i} />;
}
function GameStatsSkeletonList() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={classes.listHost}><Stack gap="var(--spacing-md)" className={classes.list}>{Array.from({
          length: 5
        }).map(_temp2)}</Stack></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(_, i) {
  return <div key={i} className={classes.skeletonRow}><Skeleton className={classes.skeletonThumb} /><div className={classes.skeletonMeta}><Skeleton className={classes.skeletonTitle} style={{
        width: `${55 + i % 3 * 15}%`
      }} /><Skeleton className={classes.skeletonValue} /></div></div>;
}
export type GameStatsCardProps = {
  className?: string;
  title?: string;
  tooltip?: string;
  nodeId?: string;
  sort: GameStatsSortKey;
  profileId?: string;
  startDate?: string;
  endDate?: string;
};
function GameStatsCardShell(t0) {
  const $ = _c(20);
  const {
    title,
    tooltip,
    children,
    className,
    nodeId
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(classes.card, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = nodeId ?? "493:20298";
  let t3;
  if ($[2] !== title) {
    t3 = <h3 className={classes.cardTitle}>{title}</h3>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== title || $[5] !== tooltip) {
    t4 = tooltip ? <Tooltip content={tooltip} side="top"><button type="button" className={classes.infoButton} aria-label={`About ${title}`}><InfoIcon className={classes.infoIcon} aria-hidden={true} /></button></Tooltip> : null;
    $[4] = title;
    $[5] = tooltip;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className={classes.cardHeaderRow}>{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className={classes.cardDivider} aria-hidden={true} />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5) {
    t7 = <header className={classes.cardHeader}>{t5}{t6}</header>;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== children) {
    t8 = <div className={classes.cardBody}>{children}</div>;
    $[13] = children;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t1 || $[16] !== t2 || $[17] !== t7 || $[18] !== t8) {
    t9 = <article className={t1} data-node-id={t2}>{t7}{t8}</article>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}

/** Figma 493:20298 — game stats card (header + up to 5 game rows) */
export function GameStatsCard(t0) {
  const $ = _c(18);
  const {
    className,
    title,
    tooltip,
    sort,
    profileId,
    startDate,
    endDate,
    nodeId
  } = t0;
  let t1;
  if ($[0] !== endDate || $[1] !== profileId || $[2] !== sort || $[3] !== startDate) {
    t1 = {
      sort,
      size: 20,
      profileId,
      startDate,
      endDate
    };
    $[0] = endDate;
    $[1] = profileId;
    $[2] = sort;
    $[3] = startDate;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data,
    isLoading
  } = useStreamStatsGamesQuery(t1);
  const isEmpty = data && data.content.length === 0;
  let t2;
  if ($[5] !== data || $[6] !== sort) {
    t2 = data ? gamesToRows(data.content, sort) : [];
    $[5] = data;
    $[6] = sort;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <GameStatsScrollList rows={t2} />;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let listContent = t3;
  if (isLoading) {
    let t4;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <GameStatsSkeletonList />;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    listContent = t4;
  } else {
    if (isEmpty) {
      let t4;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <GameStatsEmptyList />;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      listContent = t4;
    }
  }
  if (!title) {
    return listContent;
  }
  let t4;
  if ($[12] !== className || $[13] !== listContent || $[14] !== nodeId || $[15] !== title || $[16] !== tooltip) {
    t4 = <GameStatsCardShell title={title} tooltip={tooltip} className={className} nodeId={nodeId}>{listContent}</GameStatsCardShell>;
    $[12] = className;
    $[13] = listContent;
    $[14] = nodeId;
    $[15] = title;
    $[16] = tooltip;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}